import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '@/context/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import { formatPrice, formatDate, formatCurrencyLive, parseCurrencyToRaw } from '@/utils/format';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Unit,
  Dimension,
  compatibleUnits,
  UNIT_LABEL_SHORT,
  pricePerDisplayFromBase,
} from '@/utils/units';
import {
  usePurchaseOrders,
  usePurchaseOrder,
  useCreatePurchaseOrder,
  useEditPurchaseOrder,
  useCancelPurchaseOrder,
  useDeletePurchaseOrder,
  useUpdatePurchaseOrder,
  useReceivePurchaseOrder,
  useRegisterSupplierPayment,
  useAdjustSupplierDebt,
  useDeleteSupplierPayment,
  useUpdateSupplierPayment,
  useSupplierPayments,
  useSupplierDebtSummary,
} from '@/hooks/usePurchaseOrders';
import { useSuppliers } from '@/hooks/useSuppliers';
import { useProducts } from '@/hooks/useProducts';
import {
  PurchaseOrderCreate,
  PurchaseOrderItemCreate,
  ReceiveItemData,
  SupplierPaymentCreate,
} from '@/services/purchaseOrders';
import { PurchaseOrder, SupplierPayment } from '@/types';
import {
  Plus,
  Eye,
  XCircle,
  Trash2,
  Package,
  DollarSign,
  ClipboardList,
  AlertCircle,
  Pencil,
  Search,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Label maps
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  confirmed: 'Confirmada',
  partial_received: 'Recibida Parcial',
  received: 'Recibida',
  cancelled: 'Cancelada',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Efectivo',
  transfer: 'Transferencia',
  card: 'Tarjeta',
  check: 'Cheque',
  ajuste: 'Ajuste',
};

const ORDER_TYPE_LABELS: Record<string, string> = {
  standard: 'Orden',
  debt_only: 'Deuda',
};

// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'draft':
      return 'bg-gray-100 text-gray-800';
    case 'confirmed':
      return 'bg-blue-100 text-blue-800';
    case 'partial_received':
      return 'bg-yellow-100 text-yellow-800';
    case 'received':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// ---------------------------------------------------------------------------
// Safe string helper (React #310 prevention)
// ---------------------------------------------------------------------------

function s(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function n(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function PurchaseOrders() {
  const { user } = useAuthStore();
  const canWrite = user?.effective_permissions?.includes('products_write') ?? false;

  // Filters
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailPOId, setDetailPOId] = useState<number | null>(null);
  const [cancelConfirmPO, setCancelConfirmPO] = useState<PurchaseOrder | null>(null);
  const [deleteConfirmPO, setDeleteConfirmPO] = useState<PurchaseOrder | null>(null);
  const [payingSupplierId, setPayingSupplierId] = useState<number | null>(null);
  const [payingPendingAmount, setPayingPendingAmount] = useState(0);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [adjustSupplier, setAdjustSupplier] = useState<{ id: number; name: string; pending: number } | null>(null);

  // Queries
  const filters = {
    supplier_id: filterSupplier ? Number(filterSupplier) : undefined,
    status: filterStatus || undefined,
  };
  const { data: orders, isLoading } = usePurchaseOrders(filters);
  const { data: debtSummary } = useSupplierDebtSummary();
  const { data: suppliers } = useSuppliers();
  const cancelPO = useCancelPurchaseOrder();
  const deletePO = useDeletePurchaseOrder();

  // Supplier options for filters
  const supplierFilterOptions = [
    { value: '', label: 'Todos los proveedores' },
    ...(suppliers?.map((sup) => ({ value: sup.id.toString(), label: sup.name })) ?? []),
  ];

  const statusFilterOptions = [
    { value: '', label: 'Todos los estados' },
    { value: 'draft', label: 'Borrador' },
    { value: 'confirmed', label: 'Confirmada' },
    { value: 'partial_received', label: 'Recibida Parcial' },
    { value: 'received', label: 'Recibida' },
    { value: 'cancelled', label: 'Cancelada' },
  ];

  const handleCancelPO = async () => {
    if (!cancelConfirmPO) return;
    try {
      await cancelPO.mutateAsync(cancelConfirmPO.id);
      setCancelConfirmPO(null);
    } catch (error) {
      console.error('Error cancelling PO:', error);
    }
  };

  const handleDeletePO = async () => {
    if (!deleteConfirmPO) return;
    try {
      await deletePO.mutateAsync(deleteConfirmPO.id);
      setDeleteConfirmPO(null);
      setDetailPOId(null);
    } catch (error) {
      console.error('Error deleting PO:', error);
    }
  };

  // Debt summary: all suppliers with orders (pending first, then settled)
  const allDebts = debtSummary ?? [];

  // Stats agregadas
  const totalDebt = allDebts.reduce((acc, d) => acc + n(d.pending_amount), 0);
  const totalOrders = orders?.length ?? 0;
  const pendingSuppliers = allDebts.filter((d) => n(d.pending_amount) > 0).length;

  return (
    <div className="space-y-6">
      {/* Header con stats integradas */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Compras</h1>
            <p className="text-sm text-gray-500 mt-1">Órdenes, deudas y pagos a proveedores</p>
          </div>
          {canWrite ? (
            <button
              onClick={() => setCreateModalOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-taupe-700 hover:bg-taupe-800 text-white text-sm font-medium rounded-xl shadow-sm hover:shadow transition-all"
            >
              <Plus className="h-4 w-4" />
              Nueva orden
            </button>
          ) : null}
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl border border-gray-200 px-4 sm:px-5 py-4 min-w-0">
            <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Deuda total</p>
            <p className={`text-lg sm:text-2xl font-bold mt-1 tabular-nums truncate ${totalDebt > 0 ? 'text-red-600' : 'text-emerald-600'}`} title={formatPrice(totalDebt)}>
              {formatPrice(totalDebt)}
            </p>
            <p className="text-xs text-gray-500 mt-0.5 truncate">{pendingSuppliers} proveedor{pendingSuppliers === 1 ? '' : 'es'} con saldo</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 px-4 sm:px-5 py-4 min-w-0">
            <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Órdenes</p>
            <p className="text-lg sm:text-2xl font-bold text-gray-900 mt-1 tabular-nums">{totalOrders}</p>
            <p className="text-xs text-gray-500 mt-0.5">en el período visible</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 px-4 sm:px-5 py-4 col-span-2 lg:col-span-1 min-w-0">
            <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Proveedores</p>
            <p className="text-lg sm:text-2xl font-bold text-gray-900 mt-1 tabular-nums">{allDebts.length}</p>
            <p className="text-xs text-gray-500 mt-0.5">con compras registradas</p>
          </div>
        </div>
      </div>

      {/* Debt summary cards */}
      {allDebts.length > 0 ? (
        <div>
          <h2 className="text-xs uppercase tracking-wide text-taupe-600 font-semibold mb-3">Cuenta corriente por proveedor</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {allDebts.map((debt) => {
              const isSettled = n(debt.pending_amount) <= 0;
              return (
                <div
                  key={debt.supplier_id}
                  className={`bg-white rounded-2xl border ${isSettled ? 'border-gray-200' : 'border-red-200 ring-1 ring-red-50'} p-4 hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`flex-shrink-0 h-9 w-9 rounded-xl flex items-center justify-center ${isSettled ? 'bg-emerald-50' : 'bg-red-50'}`}>
                      <span className={`text-base font-bold ${isSettled ? 'text-emerald-600' : 'text-red-600'}`}>
                        {isSettled ? '✓' : '!'}
                      </span>
                    </div>
                    <p className="font-semibold text-gray-900 truncate flex-1">{s(debt.supplier_name)}</p>
                  </div>
                  <div className="mb-3">
                    {isSettled ? (
                      <>
                        <p className="text-xs text-gray-500">Saldo</p>
                        <p className="text-xl font-bold text-emerald-600">Al día</p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-gray-500">Saldo pendiente</p>
                        <p className="text-xl sm:text-2xl font-bold text-red-600 tabular-nums truncate" title={formatPrice(debt.pending_amount)}>{formatPrice(debt.pending_amount)}</p>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 space-y-0.5 pb-3 border-b border-gray-100">
                    <p>{s(debt.purchase_order_count)} {n(debt.purchase_order_count) === 1 ? 'orden' : 'órdenes'}</p>
                    <p>Total: <span className="text-gray-700 font-medium">{formatPrice(debt.total_debt)}</span></p>
                    <p>Pagado: <span className="text-gray-700 font-medium">{formatPrice(debt.total_paid)}</span></p>
                  </div>
                  {canWrite ? (
                    <button
                      onClick={() => {
                        setPayingSupplierId(debt.supplier_id);
                        setPayingPendingAmount(n(debt.pending_amount));
                      }}
                      className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${
                        isSettled
                          ? 'text-gray-600 bg-gray-50 hover:bg-gray-100'
                          : 'text-white bg-taupe-700 hover:bg-taupe-800'
                      }`}
                    >
                      {isSettled ? 'Ver historial' : 'Registrar pago'}
                    </button>
                  ) : null}
                  {isAdmin && !isSettled ? (
                    <button
                      onClick={() => setAdjustSupplier({ id: debt.supplier_id, name: s(debt.supplier_name), pending: n(debt.pending_amount) })}
                      className="mt-2 w-full text-xs font-medium text-taupe-600 hover:text-taupe-800"
                    >
                      Ajustar / cerrar deuda
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <Select
              options={supplierFilterOptions}
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              label="Proveedor"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Select
              options={statusFilterOptions}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Estado"
            />
          </div>
        </div>
      </div>

      {/* Orders table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <span className="h-5 w-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-2"></span>
            Cargando...
          </div>
        ) : orders && orders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-taupe-600 uppercase tracking-wider">
                    OC
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-taupe-600 uppercase tracking-wider">
                    Proveedor
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-taupe-600 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-5 py-3 text-right text-[11px] font-semibold text-taupe-600 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-taupe-600 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-taupe-600 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-5 py-3 text-right text-[11px] font-semibold text-taupe-600 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {orders.map((po) => {
                  const canCancel =
                    po.status !== 'cancelled' &&
                    po.status !== 'received';

                  return (
                    <tr key={po.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-5 py-3.5 whitespace-nowrap text-sm font-mono font-medium text-taupe-700">
                        {s(po.po_number)}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-sm font-medium text-gray-900">
                        {s(po.supplier_name)}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-500">
                        {ORDER_TYPE_LABELS[po.order_type] ? s(ORDER_TYPE_LABELS[po.order_type]) : s(po.order_type)}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                        {formatPrice(po.total_amount)}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusBadgeClass(po.status)}`}
                        >
                          {STATUS_LABELS[po.status] ? s(STATUS_LABELS[po.status]) : s(po.status)}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(po.order_date || po.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDetailPOId(po.id)}
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canWrite && canCancel ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCancelConfirmPO(po)}
                              title="Cancelar"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {canWrite ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirmPO(po)}
                              title="Eliminar"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            No hay ordenes de compra
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Nueva Orden de Compra"
        size="xl"
      >
        <CreatePurchaseOrderForm
          onSuccess={() => setCreateModalOpen(false)}
        />
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={detailPOId !== null}
        onClose={() => setDetailPOId(null)}
        title="Detalle de Orden de Compra"
        size="xl"
      >
        {detailPOId !== null ? (
          <PurchaseOrderDetail
            poId={detailPOId}
            canWrite={canWrite}
            onClose={() => setDetailPOId(null)}
          />
        ) : null}
      </Modal>

      {/* Cancel confirmation */}
      <Modal
        isOpen={cancelConfirmPO !== null}
        onClose={() => setCancelConfirmPO(null)}
        title="Cancelar Orden de Compra"
        size="sm"
      >
        {cancelConfirmPO !== null ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 bg-red-50 text-red-800 p-3 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>
                Esta accion no se puede deshacer. Se cancelara la orden{' '}
                <strong>{s(cancelConfirmPO.po_number)}</strong>.
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setCancelConfirmPO(null)}>
                Volver
              </Button>
              <Button
                variant="danger"
                onClick={handleCancelPO}
                loading={cancelPO.isPending}
              >
                Cancelar Orden
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={deleteConfirmPO !== null}
        onClose={() => setDeleteConfirmPO(null)}
        title="Eliminar Orden de Compra"
        size="sm"
      >
        {deleteConfirmPO !== null ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 bg-red-50 text-red-800 p-3 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>
                Se eliminara permanentemente la orden{' '}
                <strong>{s(deleteConfirmPO.po_number)}</strong> y todos sus datos asociados.
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirmPO(null)}>
                Volver
              </Button>
              <Button
                variant="danger"
                onClick={handleDeletePO}
                loading={deletePO.isPending}
              >
                Eliminar
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Supplier Payment Modal */}
      <Modal
        isOpen={payingSupplierId !== null}
        onClose={() => setPayingSupplierId(null)}
        title="Pago a Proveedor"
        size="md"
      >
        {payingSupplierId !== null ? (
          <SupplierPaymentForm
            supplierId={payingSupplierId}
            pendingAmount={payingPendingAmount}
            onSuccess={() => setPayingSupplierId(null)}
          />
        ) : null}
      </Modal>

      {/* Ajuste de deuda */}
      <Modal
        isOpen={adjustSupplier !== null}
        onClose={() => setAdjustSupplier(null)}
        title="Ajustar / cerrar deuda"
        size="md"
      >
        {adjustSupplier ? (
          <AdjustDebtForm
            supplierId={adjustSupplier.id}
            supplierName={adjustSupplier.name}
            pending={adjustSupplier.pending}
            onDone={() => setAdjustSupplier(null)}
          />
        ) : null}
      </Modal>
    </div>
  );
}

// ===========================================================================
// Ajuste / cierre de deuda de proveedor (conciliación, no mueve caja)
// ===========================================================================
function AdjustDebtForm({
  supplierId,
  supplierName,
  pending,
  onDone,
}: {
  supplierId: number;
  supplierName: string;
  pending: number;
  onDone: () => void;
}) {
  const adjust = useAdjustSupplierDebt();
  // Dos modos: fijar el saldo exacto (recomendado, permite corregir errores incluso
  // saldos negativos) o ajustar por un monto (reducir/aumentar).
  const [mode, setMode] = useState<'setBalance' | 'byAmount'>('setBalance');
  const [direction, setDirection] = useState<'reduce' | 'increase'>('reduce');
  const [amount, setAmount] = useState<string>('');
  const [target, setTarget] = useState<string>('0'); // nuevo saldo deseado
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const amtNum = Number(parseCurrencyToRaw(amount)) || 0;
  const targetNum = Number(parseCurrencyToRaw(target)) || 0;

  // Ajuste efectivo (delta sobre el saldo actual) según el modo elegido.
  const effDelta = mode === 'setBalance'
    ? targetNum - pending
    : (direction === 'reduce' ? -amtNum : amtNum);
  const effAmount = Math.abs(effDelta);
  const effDirection: 'reduce' | 'increase' = effDelta < 0 ? 'reduce' : 'increase';
  const resulting = pending + effDelta;

  const submit = async () => {
    setError('');
    if (effAmount <= 0) { setError('El ajuste no cambia el saldo'); return; }
    if (!reason.trim()) { setError('Escribí un motivo'); return; }
    try {
      await adjust.mutateAsync({ supplierId, data: { amount: effAmount, direction: effDirection, reason: reason.trim() } });
      onDone();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo ajustar la deuda');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-cream-50 border border-cream-200 rounded-xl p-3 text-center">
        <p className="text-xs uppercase tracking-wide text-taupe-500">Saldo actual · {supplierName}</p>
        <p className={`text-2xl font-bold tabular-nums ${pending < 0 ? 'text-amber-600' : 'text-red-600'}`}>{formatPrice(pending)}</p>
        {pending < 0 && (
          <p className="text-xs text-amber-600 mt-1">Saldo negativo (a favor). Fijalo en $0 para corregirlo.</p>
        )}
      </div>

      <p className="text-sm text-gray-600">
        Esto <strong>emprolija el saldo</strong> (conciliación). <strong>No mueve la caja</strong> ni cuenta como pago real, y queda registrado en la auditoría.
      </p>

      <div className="inline-flex p-0.5 bg-gray-100 rounded-lg">
        <button type="button" onClick={() => setMode('setBalance')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md ${mode === 'setBalance' ? 'bg-white text-taupe-800 shadow-sm' : 'text-gray-500'}`}>
          Fijar saldo exacto
        </button>
        <button type="button" onClick={() => setMode('byAmount')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md ${mode === 'byAmount' ? 'bg-white text-taupe-800 shadow-sm' : 'text-gray-500'}`}>
          Ajustar por monto
        </button>
      </div>

      {mode === 'setBalance' ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nuevo saldo</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={formatCurrencyLive(target)}
              onChange={(e) => setTarget(parseCurrencyToRaw(e.target.value))}
              className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400"
            />
          </div>
          <button type="button" onClick={() => setTarget('0')} className="text-xs text-taupe-600 hover:text-taupe-800 mt-1">
            Cerrar en $0
          </button>
          <p className="text-xs text-gray-500 mt-1">
            Ajuste a aplicar: <strong>{effDelta >= 0 ? '+' : '−'}{formatPrice(effAmount)}</strong> · Saldo resultante: <strong>{formatPrice(resulting)}</strong>
          </p>
        </div>
      ) : (
        <div>
          <div className="inline-flex p-0.5 bg-gray-100 rounded-lg mb-2">
            <button type="button" onClick={() => setDirection('reduce')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${direction === 'reduce' ? 'bg-white text-taupe-800 shadow-sm' : 'text-gray-500'}`}>
              Reducir
            </button>
            <button type="button" onClick={() => setDirection('increase')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${direction === 'increase' ? 'bg-white text-taupe-800 shadow-sm' : 'text-gray-500'}`}>
              Aumentar
            </button>
          </div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Monto del ajuste</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={formatCurrencyLive(amount)}
              onChange={(e) => setAmount(parseCurrencyToRaw(e.target.value))}
              className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Saldo resultante: <strong>{formatPrice(resulting)}</strong></p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Motivo *</label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ej: ajuste de carga inicial / corrección de error"
          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400"
        />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="secondary" onClick={onDone}>Cancelar</Button>
        <Button onClick={submit} loading={adjust.isPending}>Confirmar ajuste</Button>
      </div>
    </div>
  );
}

// ===========================================================================
// Create Purchase Order Form
// ===========================================================================

function CreatePurchaseOrderForm({ onSuccess, editPO }: { onSuccess: () => void; editPO?: PurchaseOrder }) {
  const editMode = !!editPO;
  // Items iniciales desde la orden a editar
  const initialEditItems = () =>
    (editPO?.items ?? []).map((it) => ({
      mode: (it.product_id ? 'existing' : 'custom') as 'existing' | 'custom',
      product_id: it.product_id ? String(it.product_id) : '',
      custom_name: it.custom_name || '',
      quantity: String(Number(it.quantity_ordered)),
      quantity_unit: ((it.quantity_unit as Unit) || 'unit'),
      product_dimension: ((it.quantity_unit && it.quantity_unit !== 'unit') ? 'weight' : 'count') as Dimension,
      unit_cost: String(Number(it.unit_cost)),
      discount_percentage: '',
      discount_amount: '',
      show_discount: false,
      buy_mode: 'unit' as const,
      pack_boxes: '',
      pack_units: '1',
      pack_price: '',
    }));

  const [orderType, setOrderType] = useState<'standard' | 'debt_only'>(editPO ? (editPO.order_type as 'standard' | 'debt_only') : 'standard');
  const [supplierId, setSupplierId] = useState(editPO?.supplier_id ? String(editPO.supplier_id) : '');
  const [orderDate, setOrderDate] = useState(editPO?.order_date ? editPO.order_date.split('T')[0] : new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState(editPO?.notes || '');

  // Remito: el stock ya fue ingresado por el empleado → la compra no vuelve a sumar stock
  const [stockAlreadyLoaded, setStockAlreadyLoaded] = useState(false);
  const [remitoNumber, setRemitoNumber] = useState('');
  const [remitoDate, setRemitoDate] = useState('');

  // Debt-only fields
  const [debtAmount, setDebtAmount] = useState('');
  const [debtAmountFormatted, setDebtAmountFormatted] = useState(false);
  const [debtDescription, setDebtDescription] = useState('');

  // Standard items
  const [items, setItems] = useState<
    {
      mode: 'existing' | 'custom';
      product_id: string;
      custom_name: string;
      quantity: string;
      quantity_unit: Unit;
      product_dimension: Dimension;
      unit_cost: string;
      discount_percentage: string;
      discount_amount: string;
      show_discount: boolean;
      // Compra por caja (UI): deriva quantity y unit_cost. buy_mode 'unit' = por unidad.
      buy_mode: 'unit' | 'pack';
      pack_boxes: string;
      pack_units: string;
      pack_price: string;
    }[]
  >(editMode ? initialEditItems : []);

  // IVA y total
  const [ivaEnabled, setIvaEnabled] = useState(editPO ? Number(editPO.iva_percent || 0) > 0 : false);
  const [ivaIncluded, setIvaIncluded] = useState(editPO?.iva_included || false);  // true = el costo cargado ya incluye IVA
  const [ivaPercent, setIvaPercent] = useState(editPO && Number(editPO.iva_percent || 0) > 0 ? String(editPO.iva_percent) : '21');

  // formatCurrencyLive / parseCurrencyToRaw ahora se importan de utils/format (versión
  // arreglada que preserva el separador decimal recién tipeado).

  const { data: suppliers } = useSuppliers();
  const { data: products } = useProducts({ is_combo: false });
  const [productSearches, setProductSearches] = useState<Record<number, string>>({});
  const [productSearchDebounced, setProductSearchDebounced] = useState('');
  const [openProductDropdown, setOpenProductDropdown] = useState<number | null>(null);
  const [selectedProductNames, setSelectedProductNames] = useState<Record<number, string>>(() => {
    if (!editPO) return {};
    const names: Record<number, string> = {};
    (editPO.items ?? []).forEach((it, idx) => { names[idx] = it.product_name || it.custom_name || ''; });
    return names;
  });

  // Debounce de búsqueda
  const currentSearch = openProductDropdown !== null ? (productSearches[openProductDropdown] ?? '') : '';
  useEffect(() => {
    const timer = setTimeout(() => setProductSearchDebounced(currentSearch), 250);
    return () => clearTimeout(timer);
  }, [currentSearch]);

  // Filtrar el buscador de items por el proveedor de la orden (opcional)
  const [filterItemsBySupplier, setFilterItemsBySupplier] = useState(false);

  // Query dinámica: siempre activa cuando dropdown abierto
  const { data: searchResults } = useProducts(
    {
      search: productSearchDebounced || undefined,
      supplier_id: filterItemsBySupplier && supplierId ? Number(supplierId) : undefined,
      limit: 50,
      is_combo: false,
    },
    { enabled: openProductDropdown !== null }
  );

  useEffect(() => {
    if (openProductDropdown === null) return;
    const handleClick = () => setOpenProductDropdown(null);
    const timer = setTimeout(() => document.addEventListener('click', handleClick), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', handleClick); };
  }, [openProductDropdown]);
  const createPO = useCreatePurchaseOrder();
  const editPOMutation = useEditPurchaseOrder();

  const supplierOptions = [
    { value: '', label: 'Seleccionar proveedor...' },
    ...(suppliers?.map((sup) => ({ value: sup.id.toString(), label: sup.name })) ?? []),
  ];


  const addItem = () => {
    setItems([
      ...items,
      {
        mode: 'existing',
        product_id: '',
        custom_name: '',
        quantity: '1',
        quantity_unit: 'unit',
        product_dimension: 'count',
        unit_cost: '',
        discount_percentage: '',
        discount_amount: '',
        show_discount: false,
        buy_mode: 'unit',
        pack_boxes: '',
        pack_units: '1',
        pack_price: '',
      },
    ]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
    // Los mapas auxiliares están indexados por posición: al borrar hay que correrlos
    // hacia abajo, si no los nombres/búsquedas quedan desalineados (parece que no borró).
    const reindex = <T,>(obj: Record<number, T>): Record<number, T> => {
      const out: Record<number, T> = {};
      Object.keys(obj).forEach((k) => {
        const i = Number(k);
        if (i < index) out[i] = obj[i];
        else if (i > index) out[i - 1] = obj[i];
      });
      return out;
    };
    setSelectedProductNames((prev) => reindex(prev));
    setProductSearches((prev) => reindex(prev));
    setOpenProductDropdown(null);
  };

  // --- Selección rápida de productos del proveedor ---
  const [showSupplierPicker, setShowSupplierPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSelected, setPickerSelected] = useState<Set<number>>(new Set());
  const [pickerQty, setPickerQty] = useState<Record<number, string>>({});

  // Productos del proveedor elegido (para el modal de selección rápida)
  const supplierProducts = (products || []).filter((p) => supplierId && p.supplier_id === Number(supplierId));

  const openSupplierPicker = () => {
    if (!supplierId) return;
    if (supplierProducts.length === 0) {
      toast.error('Ese proveedor no tiene productos cargados');
      return;
    }
    // Preseleccionar todos los que todavía no estén en la orden
    const already = new Set(
      items.filter((i) => i.mode === 'existing' && i.product_id).map((i) => Number(i.product_id))
    );
    setPickerSelected(new Set(supplierProducts.filter((p) => !already.has(p.id)).map((p) => p.id)));
    // Cantidad por defecto = 1 para cada producto (editable en el modal)
    setPickerQty(Object.fromEntries(supplierProducts.map((p) => [p.id, '1'])));
    setPickerSearch('');
    setShowSupplierPicker(true);
  };

  // Agrega al pedido los productos indicados, con su costo y cantidad por defecto (editable después).
  const addSupplierProductsToItems = (prods: typeof supplierProducts, qtyFor?: (id: number) => string) => {
    const existingIds = new Set(
      items.filter((i) => i.mode === 'existing' && i.product_id).map((i) => Number(i.product_id))
    );
    const toAdd = prods.filter((p) => !existingIds.has(p.id));
    if (toAdd.length === 0) return;
    const newItems = toAdd.map((p) => {
      const dim: Dimension = (p.dimension as Dimension) || 'count';
      const dispUnit: Unit = (p.display_unit as Unit) || 'unit';
      const qty = qtyFor?.(p.id);
      return {
        mode: 'existing' as const,
        product_id: String(p.id),
        custom_name: '',
        quantity: qty && Number(qty) > 0 ? qty : '1',
        quantity_unit: dispUnit,
        product_dimension: dim,
        unit_cost: p.cost_price ? String(pricePerDisplayFromBase(Number(p.cost_price), dispUnit)) : '',
        discount_percentage: '',
        discount_amount: '',
        show_discount: false,
        buy_mode: 'unit' as const,
        pack_boxes: '',
        pack_units: p.units_per_pack && p.units_per_pack > 1 ? String(p.units_per_pack) : '1',
        pack_price: '',
      };
    });
    const baseIndex = items.length;
    setItems([...items, ...newItems]);
    setSelectedProductNames((prev) => {
      const next = { ...prev };
      toAdd.forEach((p, i) => { next[baseIndex + i] = p.name; });
      return next;
    });
    toast.success(`${toAdd.length} producto${toAdd.length !== 1 ? 's' : ''} cargado${toAdd.length !== 1 ? 's' : ''}`);
  };

  const confirmSupplierPicker = () => {
    const chosen = supplierProducts.filter((p) => pickerSelected.has(p.id));
    if (chosen.length === 0) {
      toast('No seleccionaste productos');
      return;
    }
    addSupplierProductsToItems(chosen, (id) => pickerQty[id] ?? '1');
    setShowSupplierPicker(false);
  };

  // Descarga el detalle del pedido (productos + cantidades, sin costos) en PDF para enviar al proveedor.
  const downloadOrderDetail = () => {
    const rows = items
      .map((it, idx) => ({
        name: it.mode === 'custom' ? it.custom_name : (selectedProductNames[idx] || ''),
        qty: `${String(Number(it.quantity) || 0)} ${UNIT_LABEL_SHORT[it.quantity_unit]}`,
        valid: !!(it.mode === 'custom' ? it.custom_name : it.product_id) && Number(it.quantity) > 0,
      }))
      .filter((r) => r.valid);
    if (rows.length === 0) {
      toast.error('Cargá productos con cantidad para el detalle');
      return;
    }
    const supplier = suppliers?.find((sp) => sp.id === Number(supplierId));
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Pedido de compra', 14, 18);
    doc.setFontSize(11);
    doc.text(`Proveedor: ${supplier?.name || '-'}`, 14, 27);
    doc.text(`Fecha: ${formatDate(orderDate)}`, 14, 33);
    autoTable(doc, {
      startY: 40,
      head: [['Cantidad', 'Producto']],
      body: rows.map((r) => [r.qty, r.name]),
      styles: { fontSize: 9, cellPadding: 1.5 },
      headStyles: { fillColor: [107, 91, 79], textColor: 255 },
      columnStyles: { 0: { cellWidth: 28, halign: 'right' } },
    });
    doc.save(`pedido-${(supplier?.name || 'proveedor').replace(/\s+/g, '-').toLowerCase()}-${orderDate}.pdf`);
  };

  const updateItem = (index: number, field: string, value: string | boolean) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-fill unit cost (en display unit) y unidad cuando se selecciona un producto existente
    if (field === 'product_id' && value) {
      const allProducts = [...(products || []), ...(searchResults || [])];
      const product = allProducts.find((p) => p.id === Number(value));
      if (product) {
        const dim: Dimension = (product.dimension as Dimension) || 'count';
        const dispUnit: Unit = (product.display_unit as Unit) || 'unit';
        updated[index].product_dimension = dim;
        updated[index].quantity_unit = dispUnit;
        // Precargar unidades por caja del producto (para el modo "por caja")
        updated[index].pack_units = product.units_per_pack && product.units_per_pack > 1
          ? String(product.units_per_pack)
          : (updated[index].pack_units || '1');
        if (product.cost_price) {
          // cost_price viene en $/base. Mostrar en $/display_unit.
          updated[index].unit_cost = String(pricePerDisplayFromBase(Number(product.cost_price), dispUnit));
        }
      }
    }

    setItems(updated);
  };

  // --- Modo "compra por caja": deriva quantity (unidades) y unit_cost ($/unidad) ---
  const applyPackDerived = (it: typeof items[number]) => {
    const boxes = Number(it.pack_boxes) || 0;
    const per = Number(it.pack_units) || 0;
    const price = Number(it.pack_price) || 0;
    const qty = boxes > 0 && per > 0 ? boxes * per : 0;
    const uc = per > 0 && price > 0 ? price / per : 0;
    return { ...it, quantity: qty ? String(qty) : '0', unit_cost: uc ? String(uc) : '' };
  };

  const setPackField = (index: number, field: 'pack_boxes' | 'pack_units' | 'pack_price', value: string) => {
    const updated = [...items];
    updated[index] = applyPackDerived({ ...updated[index], [field]: value });
    setItems(updated);
  };

  const setBuyMode = (index: number, mode: 'unit' | 'pack') => {
    const updated = [...items];
    let it = { ...updated[index], buy_mode: mode };
    if (mode === 'pack') {
      it = applyPackDerived(it);
    }
    updated[index] = it;
    setItems(updated);
  };

  const updateItemUnit = (index: number, newUnit: Unit) => {
    const updated = [...items];
    // Si es item custom (producto nuevo), inferir la dimension desde la unidad elegida
    // para que al recibirse se cree el producto con la dimension correcta.
    const isCustom = updated[index].mode === 'custom';
    const inferredDimension: Dimension = newUnit === 'unit' ? 'count' : 'weight';
    updated[index] = {
      ...updated[index],
      quantity_unit: newUnit,
      product_dimension: isCustom ? inferredDimension : updated[index].product_dimension,
    };
    setItems(updated);
  };

  // Calcular line_total por item (con descuento aplicado) y totales agregados
  const itemLineTotal = (item: typeof items[number]): number => {
    const gross = n(item.quantity) * n(item.unit_cost);
    const pct = n(item.discount_percentage);
    const amt = n(item.discount_amount);
    let total = gross;
    if (pct > 0) total -= total * (pct / 100);
    if (amt > 0) total -= amt;
    return Math.max(0, total);
  };

  const itemsSubtotal = items.reduce((acc, item) => acc + itemLineTotal(item), 0);
  const itemsGross = items.reduce((acc, item) => acc + n(item.quantity) * n(item.unit_cost), 0);
  const itemsDiscount = itemsGross - itemsSubtotal;

  // IVA y total final
  const ivaPct = ivaEnabled ? n(ivaPercent) : 0;
  let ivaAmount = 0;
  let totalFinal = itemsSubtotal;
  if (ivaEnabled && ivaPct > 0) {
    if (ivaIncluded) {
      // Los precios cargados ya tienen IVA. itemsSubtotal es el TOTAL final.
      // Separamos el IVA: subtotal_neto = total / (1 + pct/100); iva = total - subtotal_neto
      const divisor = 1 + ivaPct / 100;
      const neto = itemsSubtotal / divisor;
      ivaAmount = itemsSubtotal - neto;
      totalFinal = itemsSubtotal;
    } else {
      // IVA NO incluido: lo sumamos
      ivaAmount = itemsSubtotal * (ivaPct / 100);
      totalFinal = itemsSubtotal + ivaAmount;
    }
  }
  const itemsTotal = totalFinal; // mantengo el nombre para compatibilidad

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: PurchaseOrderCreate = {
      supplier_id: supplierId ? Number(supplierId) : undefined,
      order_type: orderType,
      order_date: orderDate || undefined,
      notes: notes || undefined,
      items: [],
    };

    if (orderType === 'debt_only') {
      payload.total_amount = n(debtAmount);
      payload.description = debtDescription || undefined;
    } else {
      payload.iva_included = ivaEnabled ? ivaIncluded : false;
      payload.iva_percent = ivaEnabled ? n(ivaPercent) : 0;
      payload.stock_already_loaded = stockAlreadyLoaded;
      if (stockAlreadyLoaded) {
        payload.remito_number = remitoNumber || undefined;
        payload.remito_date = remitoDate || undefined;
      }
      payload.items = items.map((item): PurchaseOrderItemCreate => {
        const base = {
          quantity_ordered: n(item.quantity),
          quantity_unit: item.quantity_unit,
          unit_cost: n(item.unit_cost),
          discount_percentage: n(item.discount_percentage),
          discount_amount: n(item.discount_amount),
        };
        if (item.mode === 'existing' && item.product_id) {
          return { ...base, product_id: Number(item.product_id) };
        }
        return { ...base, custom_name: item.custom_name || 'Producto sin nombre' };
      });
    }

    try {
      if (editMode && editPO) {
        await editPOMutation.mutateAsync({ id: editPO.id, data: payload });
        toast.success('Orden actualizada');
      } else {
        await createPO.mutateAsync(payload);
      }
      onSuccess();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || 'No se pudo guardar la orden';
      toast.error(msg);
      console.error('Error saving PO:', error);
    }
  };

  const isValid =
    orderType === 'debt_only'
      ? !!supplierId && n(debtAmount) > 0
      : !!supplierId && items.length > 0 && items.every((item) => {
          const hasProduct = item.mode === 'existing' ? !!item.product_id : !!item.custom_name;
          return hasProduct && n(item.quantity) > 0 && n(item.unit_cost) > 0;
        });

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {editMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
          Editando la orden <strong>{editPO?.po_number}</strong>. Podés corregir productos, cantidades, costos e IVA antes de recibir la mercadería.
        </div>
      )}
      {/* Order type toggle */}
      <div className={editMode ? 'hidden' : ''}>
        <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-2">Tipo de orden</label>
        <div className="flex gap-2 p-1 bg-cream-100 rounded-xl">
          <button
            type="button"
            onClick={() => setOrderType('standard')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              orderType === 'standard'
                ? 'bg-white text-taupe-800 shadow-sm ring-1 ring-taupe-100'
                : 'text-taupe-500 hover:text-taupe-800'
            }`}
          >
            <Package className="h-4 w-4" />
            Orden completa
          </button>
          <button
            type="button"
            onClick={() => setOrderType('debt_only')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              orderType === 'debt_only'
                ? 'bg-white text-taupe-800 shadow-sm ring-1 ring-taupe-100'
                : 'text-taupe-500 hover:text-taupe-800'
            }`}
          >
            <DollarSign className="h-4 w-4" />
            Solo registrar deuda
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Supplier */}
        <div>
          <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-1.5">Proveedor *</label>
          <select
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors"
          >
            {supplierOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Order date */}
        <div>
          <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-1.5">Fecha de la compra</label>
          <input
            type="date"
            value={orderDate}
            onChange={(e) => setOrderDate(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors"
          />
        </div>
      </div>

      {/* Debt-only mode */}
      {orderType === 'debt_only' ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monto total *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={debtAmountFormatted && n(debtAmount) > 0
                  ? n(debtAmount).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : debtAmount}
                onChange={(e) => {
                  setDebtAmountFormatted(false);
                  setDebtAmount(e.target.value.replace(/[^\d.,]/g, ''));
                }}
                onFocus={() => {
                  setDebtAmountFormatted(false);
                  if (n(debtAmount) > 0) setDebtAmount(String(n(debtAmount)));
                }}
                onBlur={() => {
                  let val = debtAmount.replace(/\./g, '').replace(',', '.');
                  const num = parseFloat(val);
                  if (!isNaN(num) && num > 0) {
                    setDebtAmount(String(num));
                    setDebtAmountFormatted(true);
                  }
                }}
                placeholder="0,00"
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
            <textarea
              value={debtDescription}
              onChange={(e) => setDebtDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
              placeholder="Descripcion de la deuda..."
            />
          </div>
        </div>
      ) : (
        /* Standard mode - items table */
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600">Items</label>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {supplierId && (
                <label className="inline-flex items-center gap-1.5 text-xs text-taupe-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={filterItemsBySupplier}
                    onChange={(e) => setFilterItemsBySupplier(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-taupe-600 focus:ring-taupe-400"
                  />
                  Solo de este proveedor
                </label>
              )}
              {supplierId && (
                <button
                  type="button"
                  onClick={openSupplierPicker}
                  title="Elegir productos de este proveedor (traen su costo por defecto)"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-taupe-700 hover:text-taupe-900 bg-cream-100 hover:bg-cream-200 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <Package className="h-4 w-4" />
                  Elegir del proveedor
                </button>
              )}
              {items.length > 0 && (
                <button
                  type="button"
                  onClick={downloadOrderDetail}
                  title="Descargar el detalle (productos y cantidades) en PDF para enviar al proveedor"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-taupe-700 hover:text-taupe-900 bg-cream-100 hover:bg-cream-200 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <ClipboardList className="h-4 w-4" />
                  Descargar detalle
                </button>
              )}
              <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-taupe-700 hover:text-taupe-900 hover:bg-taupe-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" />
                Agregar item
              </button>
            </div>
          </div>

          {items.length > 0 ? (
            <div className="space-y-3">
              {items.map((item, idx) => {
                const lineGross = n(item.quantity) * n(item.unit_cost);
                const lineTotal = itemLineTotal(item);
                const hasDiscount = n(item.discount_percentage) > 0 || n(item.discount_amount) > 0;
                const isWeight = item.product_dimension === 'weight';
                // En modo "Nuevo" mostramos las 3 unidades para que el usuario pueda definir
                // si el producto nuevo es por unidad o por peso. En modo "Existente" solo
                // las compatibles con la dimension del producto ya cargado.
                const unitOpts: Unit[] = item.mode === 'custom'
                  ? ['unit', 'gram', 'kilogram']
                  : compatibleUnits(item.product_dimension);
                return (
                  <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-4 hover:border-gray-300 transition-colors">
                    {/* Top: producto + delete */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex-1 min-w-0 space-y-2">
                        {/* Modo */}
                        <div className="inline-flex p-0.5 bg-gray-100 rounded-lg">
                          <button
                            type="button"
                            onClick={() => updateItem(idx, 'mode', 'existing')}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                              item.mode === 'existing'
                                ? 'bg-white text-taupe-800 shadow-sm'
                                : 'text-gray-500'
                            }`}
                          >
                            Producto existente
                          </button>
                          <button
                            type="button"
                            onClick={() => updateItem(idx, 'mode', 'custom')}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                              item.mode === 'custom'
                                ? 'bg-white text-taupe-800 shadow-sm'
                                : 'text-gray-500'
                            }`}
                          >
                            Nuevo
                          </button>
                        </div>
                        {/* Producto */}
                        {item.mode === 'existing' ? (
                          <ProductSearchSelect
                            value={selectedProductNames[idx] || ''}
                            searchValue={openProductDropdown === idx ? (productSearches[idx] ?? '') : undefined}
                            isOpen={openProductDropdown === idx}
                            results={openProductDropdown === idx ? (searchResults || []) : []}
                            isSearching={!!currentSearch}
                            onFocus={() => {
                              setOpenProductDropdown(idx);
                              setProductSearches({ ...productSearches, [idx]: '' });
                            }}
                            onSearch={(val) => {
                              setProductSearches({ ...productSearches, [idx]: val });
                              setOpenProductDropdown(idx);
                            }}
                            onSelect={(p) => {
                              updateItem(idx, 'product_id', p.id.toString());
                              setSelectedProductNames({ ...selectedProductNames, [idx]: p.name });
                              setOpenProductDropdown(null);
                              setProductSearches({ ...productSearches, [idx]: '' });
                            }}
                          />
                        ) : (
                          <input
                            type="text"
                            value={item.custom_name}
                            onChange={(e) => updateItem(idx, 'custom_name', e.target.value)}
                            placeholder="Nombre del producto nuevo"
                            className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors"
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        title="Eliminar item"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Bottom: cantidad / costo / subtotal (con modo por unidad o por caja) */}
                    <div className="pt-3 border-t border-gray-100">
                      {/* Selector por unidad / por caja (solo productos por unidad) */}
                      {!isWeight && (
                        <div className="inline-flex p-0.5 bg-gray-100 rounded-lg mb-3">
                          <button
                            type="button"
                            onClick={() => setBuyMode(idx, 'unit')}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${item.buy_mode !== 'pack' ? 'bg-white text-taupe-800 shadow-sm' : 'text-gray-500'}`}
                          >
                            Por unidad
                          </button>
                          <button
                            type="button"
                            onClick={() => setBuyMode(idx, 'pack')}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${item.buy_mode === 'pack' ? 'bg-white text-taupe-800 shadow-sm' : 'text-gray-500'}`}
                          >
                            Por caja
                          </button>
                        </div>
                      )}

                      {item.buy_mode === 'pack' && !isWeight ? (
                        <div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-[10px] uppercase tracking-wide font-semibold text-taupe-600 mb-1">Cajas</label>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={item.pack_boxes}
                                onChange={(e) => setPackField(idx, 'pack_boxes', e.target.value)}
                                onFocus={(e) => e.target.select()}
                                className="w-full text-sm text-right px-2 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase tracking-wide font-semibold text-taupe-600 mb-1">Unid. por caja</label>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={item.pack_units}
                                onChange={(e) => setPackField(idx, 'pack_units', e.target.value)}
                                onFocus={(e) => e.target.select()}
                                className="w-full text-sm text-right px-2 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase tracking-wide font-semibold text-taupe-600 mb-1">Precio caja</label>
                              <div className="relative">
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={formatCurrencyLive(item.pack_price)}
                                  onChange={(e) => setPackField(idx, 'pack_price', parseCurrencyToRaw(e.target.value))}
                                  onFocus={(e) => e.target.select()}
                                  placeholder="0"
                                  className="w-full text-sm text-right pl-6 pr-2 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase tracking-wide font-semibold text-taupe-600 mb-1">Subtotal</label>
                              <div className="px-3 py-2 bg-cream-50 rounded-lg text-right">
                                {hasDiscount && (
                                  <div className="text-xs text-gray-400 line-through">{formatPrice(lineGross)}</div>
                                )}
                                <div className="text-sm font-bold text-taupe-900">{formatPrice(lineTotal)}</div>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-taupe-500 mt-1.5">
                            = <strong>{Number(item.quantity) || 0}</strong> unidades a stock · costo unitario <strong>{item.unit_cost ? formatPrice(item.unit_cost) : '$0'}</strong>
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-[10px] uppercase tracking-wide font-semibold text-taupe-600 mb-1">Cantidad</label>
                            <div className="flex gap-1.5">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                onFocus={(e) => e.target.select()}
                                min="0"
                                step={isWeight ? '0.01' : '1'}
                                className="flex-1 min-w-0 text-sm text-right px-2 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors"
                              />
                              {unitOpts.length > 1 && (
                                <select
                                  value={item.quantity_unit}
                                  onChange={(e) => updateItemUnit(idx, e.target.value as Unit)}
                                  className="text-sm border border-gray-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors"
                                >
                                  {unitOpts.map((u) => (
                                    <option key={u} value={u}>{UNIT_LABEL_SHORT[u]}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-wide font-semibold text-taupe-600 mb-1">Costo unit.</label>
                            <div className="relative">
                              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={formatCurrencyLive(item.unit_cost)}
                                onChange={(e) => updateItem(idx, 'unit_cost', parseCurrencyToRaw(e.target.value))}
                                onFocus={(e) => e.target.select()}
                                placeholder="0"
                                className="w-full text-sm text-right pl-6 pr-9 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors"
                              />
                              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">/{UNIT_LABEL_SHORT[item.quantity_unit]}</span>
                            </div>
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-wide font-semibold text-taupe-600 mb-1">Subtotal</label>
                            <div className="px-3 py-2 bg-cream-50 rounded-lg text-right">
                              {hasDiscount && (
                                <div className="text-xs text-gray-400 line-through">{formatPrice(lineGross)}</div>
                              )}
                              <div className="text-sm font-bold text-taupe-900">{formatPrice(lineTotal)}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Descuento */}
                    <div className="mt-3">
                      {!item.show_discount && !hasDiscount ? (
                        <button
                          type="button"
                          onClick={() => updateItem(idx, 'show_discount', true)}
                          className="text-xs text-taupe-600 hover:text-taupe-800 font-medium"
                        >
                          + Agregar descuento
                        </button>
                      ) : (
                        <div className="bg-cream-100/60 rounded-lg p-3 grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] uppercase tracking-wide font-semibold text-taupe-600 mb-1">Descuento %</label>
                            <input
                              type="number"
                              value={item.discount_percentage}
                              onChange={(e) => updateItem(idx, 'discount_percentage', e.target.value)}
                              onFocus={(e) => e.target.select()}
                              min="0" max="100" step="0.01"
                              placeholder="0"
                              className="w-full text-sm text-right px-2 py-1.5 border border-cream-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-wide font-semibold text-taupe-600 mb-1">o monto fijo $</label>
                            <input
                              type="number"
                              value={item.discount_amount}
                              onChange={(e) => updateItem(idx, 'discount_amount', e.target.value)}
                              onFocus={(e) => e.target.select()}
                              min="0" step="0.01"
                              placeholder="0"
                              className="w-full text-sm text-right px-2 py-1.5 border border-cream-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Resumen con desglose: subtotal, descuentos, IVA opcional, total */}
              <div className="bg-gradient-to-br from-cream-50 to-taupe-50 rounded-2xl border border-taupe-200 p-4 space-y-2.5">
                {itemsDiscount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-taupe-600">Subtotal sin descuentos</span>
                    <span className="text-taupe-700 line-through">{formatPrice(itemsGross)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-taupe-600">Subtotal</span>
                  <span className="font-medium text-taupe-900">{formatPrice(itemsSubtotal)}</span>
                </div>
                {itemsDiscount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-taupe-600">Descuentos aplicados</span>
                    <span className="font-medium text-emerald-700">-{formatPrice(itemsDiscount)}</span>
                  </div>
                )}

                {/* Toggle IVA */}
                <div className="border-t border-taupe-200/60 pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-taupe-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ivaEnabled}
                        onChange={(e) => setIvaEnabled(e.target.checked)}
                        className="w-4 h-4 rounded border-taupe-300 text-taupe-700 focus:ring-taupe-400"
                      />
                      <span className="font-medium">Aplicar IVA</span>
                    </label>
                    {ivaEnabled && (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={ivaPercent}
                          onChange={(e) => setIvaPercent(e.target.value)}
                          min="0" max="100" step="0.01"
                          className="w-16 text-right px-2 py-1 text-sm border border-taupe-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400"
                        />
                        <span className="text-sm text-taupe-600">%</span>
                      </div>
                    )}
                  </div>
                  {ivaEnabled && (
                    <>
                      <label className="flex items-center gap-2 text-xs text-taupe-600 cursor-pointer ml-6">
                        <input
                          type="checkbox"
                          checked={ivaIncluded}
                          onChange={(e) => setIvaIncluded(e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-taupe-300 text-taupe-700 focus:ring-taupe-400"
                        />
                        El precio del proveedor ya incluye IVA
                      </label>
                      <div className="flex justify-between text-sm">
                        <span className="text-taupe-600">IVA ({ivaPercent}%) {ivaIncluded ? '(incluido)' : ''}</span>
                        <span className="font-medium text-taupe-900">{formatPrice(ivaAmount)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Remito: el stock ya fue ingresado */}
                <div className="border-t border-taupe-200/60 pt-3 space-y-2">
                  <label className="flex items-start gap-2 text-sm text-taupe-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={stockAlreadyLoaded}
                      onChange={(e) => setStockAlreadyLoaded(e.target.checked)}
                      className="w-4 h-4 mt-0.5 rounded border-taupe-300 text-taupe-700 focus:ring-taupe-400"
                    />
                    <span>
                      <span className="font-medium">El stock ya fue ingresado por remito</span>
                      <span className="block text-xs text-taupe-500">
                        No vuelve a sumar al inventario (lo cargó el empleado). Solo registra la deuda y los costos.
                      </span>
                    </span>
                  </label>
                  {stockAlreadyLoaded && (
                    <div className="grid grid-cols-2 gap-2 ml-6">
                      <div>
                        <label className="block text-xs text-taupe-600 mb-1">N° de remito</label>
                        <input
                          type="text"
                          value={remitoNumber}
                          onChange={(e) => setRemitoNumber(e.target.value)}
                          placeholder="Ej: 0001-00012345"
                          className="w-full px-2 py-1.5 text-sm border border-taupe-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-taupe-600 mb-1">Fecha del remito</label>
                        <input
                          type="date"
                          value={remitoDate}
                          onChange={(e) => setRemitoDate(e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-taupe-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Total final */}
                <div className="border-t border-taupe-300 pt-3 flex justify-between items-center">
                  <span className="text-sm font-semibold text-taupe-700">Total a pagar al proveedor</span>
                  <span className="text-2xl font-bold text-taupe-900">{formatPrice(itemsTotal)}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center bg-gray-50/40">
              <Package className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No hay items todavía</p>
              <p className="text-xs text-gray-400 mt-0.5">Click en "Agregar item" para empezar</p>
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      <div>
        <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-1.5">Notas</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors resize-none"
          placeholder="Notas adicionales (opcional)..."
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
        <button
          type="submit"
          disabled={!isValid || createPO.isPending || editPOMutation.isPending}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-taupe-700 hover:bg-taupe-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl shadow-sm hover:shadow transition-all"
        >
          {(createPO.isPending || editPOMutation.isPending) ? (
            <>
              <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {editMode ? 'Guardando...' : 'Creando...'}
            </>
          ) : (
            <>
              <Package className="h-4 w-4" />
              {editMode ? 'Guardar cambios' : 'Crear Orden'}
            </>
          )}
        </button>
      </div>

      {/* Modal: selección rápida de productos del proveedor */}
      <Modal
        isOpen={showSupplierPicker}
        onClose={() => setShowSupplierPicker(false)}
        title="Elegir productos del proveedor"
        size="lg"
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Buscar producto…"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-taupe-400"
              />
            </div>
            {(() => {
              const visible = supplierProducts.filter((p) =>
                !pickerSearch || p.name.toLowerCase().includes(pickerSearch.toLowerCase()) || (p.code || '').toLowerCase().includes(pickerSearch.toLowerCase())
              );
              const allVisibleSelected = visible.length > 0 && visible.every((p) => pickerSelected.has(p.id));
              return (
                <button
                  type="button"
                  onClick={() => {
                    setPickerSelected((prev) => {
                      const next = new Set(prev);
                      if (allVisibleSelected) visible.forEach((p) => next.delete(p.id));
                      else visible.forEach((p) => next.add(p.id));
                      return next;
                    });
                  }}
                  className="text-sm font-medium text-taupe-700 hover:text-taupe-900 whitespace-nowrap px-3 py-2"
                >
                  {allVisibleSelected ? 'Quitar todos' : 'Seleccionar todos'}
                </button>
              );
            })()}
          </div>

          <div className="max-h-80 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100">
            {supplierProducts
              .filter((p) =>
                !pickerSearch || p.name.toLowerCase().includes(pickerSearch.toLowerCase()) || (p.code || '').toLowerCase().includes(pickerSearch.toLowerCase())
              )
              .map((p) => {
                const dispUnit = (p.display_unit as Unit) || 'unit';
                const checked = pickerSelected.has(p.id);
                return (
                  <label key={p.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setPickerSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                          return next;
                        });
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-taupe-600 focus:ring-taupe-400"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate block">{p.name}</span>
                      {p.code && <span className="text-xs text-gray-400">{p.code}</span>}
                    </span>
                    {/* Cantidad (editable acá; después también en la fila de abajo) */}
                    <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.preventDefault()}>
                      <input
                        type="number"
                        min="0"
                        step={dispUnit === 'unit' ? '1' : 'any'}
                        value={pickerQty[p.id] ?? '1'}
                        onChange={(e) => setPickerQty((prev) => ({ ...prev, [p.id]: e.target.value }))}
                        onClick={(e) => e.stopPropagation()}
                        className="w-16 text-sm text-right px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400"
                      />
                      <span className="text-xs text-gray-400 w-5">{UNIT_LABEL_SHORT[dispUnit]}</span>
                    </span>
                    <span className="text-sm text-gray-500 whitespace-nowrap w-24 text-right">
                      {p.cost_price != null
                        ? `${formatPrice(pricePerDisplayFromBase(Number(p.cost_price), dispUnit))}${dispUnit !== 'unit' ? `/${dispUnit === 'kilogram' ? 'kg' : 'g'}` : ''}`
                        : 'sin costo'}
                    </span>
                  </label>
                );
              })}
          </div>

          <div className="flex items-center justify-between pt-1">
            <span className="text-sm text-gray-500">{pickerSelected.size} seleccionado{pickerSelected.size !== 1 ? 's' : ''}</span>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setShowSupplierPicker(false)}>Cancelar</Button>
              <Button type="button" onClick={confirmSupplierPicker}>Agregar seleccionados</Button>
            </div>
          </div>
        </div>
      </Modal>
    </form>
  );
}

// ===========================================================================
// Purchase Order Detail
// ===========================================================================

function PurchaseOrderDetail({
  poId,
  canWrite,
}: {
  poId: number;
  canWrite: boolean;
  onClose: () => void;
}) {
  const { data: po, isLoading } = usePurchaseOrder(poId);
  const updatePO = useUpdatePurchaseOrder();
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [editDate, setEditDate] = useState('');

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">Cargando...</div>;
  }

  if (!po) {
    return <div className="text-center py-8 text-gray-500">Orden no encontrada</div>;
  }

  const canReceive =
    po.status !== 'received' &&
    po.status !== 'cancelled' &&
    po.order_type === 'standard';

  // Descarga el detalle del pedido (productos + cantidades, sin costos) en PDF.
  const downloadDetail = () => {
    const rows = (po.items ?? [])
      .map((it) => ({
        name: it.product_name || it.custom_name || '-',
        qty: `${String(Number(it.quantity_ordered))} ${UNIT_LABEL_SHORT[(it.quantity_unit as Unit) || 'unit']}`,
      }));
    if (rows.length === 0) {
      toast.error('El pedido no tiene items');
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Pedido de compra', 14, 18);
    doc.setFontSize(11);
    doc.text(`Proveedor: ${s(po.supplier_name) || '-'}`, 14, 27);
    doc.text(`Fecha: ${formatDate(po.order_date)}`, 14, 33);
    autoTable(doc, {
      startY: 40,
      head: [['Cantidad', 'Producto']],
      body: rows.map((r) => [r.qty, r.name]),
      styles: { fontSize: 9, cellPadding: 1.5 },
      headStyles: { fillColor: [107, 91, 79], textColor: 255 },
      columnStyles: { 0: { cellWidth: 28, halign: 'right' } },
    });
    doc.save(`pedido-${(s(po.supplier_name) || 'proveedor').replace(/\s+/g, '-').toLowerCase()}.pdf`);
  };

  return (
    <div className="space-y-6">
      {/* Header info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase">Orden</p>
            <p className="text-sm font-bold text-gray-900">{s(po.po_number)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Proveedor</p>
            <p className="text-sm font-medium text-gray-900">{s(po.supplier_name)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Fecha</p>
            {editingDate ? (
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  className="text-sm border rounded px-1 py-0.5"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                />
                <button
                  className="text-green-600 text-xs font-medium"
                  onClick={async () => {
                    if (editDate) {
                      await updatePO.mutateAsync({ id: po.id, data: { order_date: editDate } });
                    }
                    setEditingDate(false);
                  }}
                >
                  OK
                </button>
                <button
                  className="text-gray-400 text-xs"
                  onClick={() => setEditingDate(false)}
                >
                  X
                </button>
              </div>
            ) : (
              <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                {formatDate(po.order_date || po.created_at)}
                {canWrite ? (
                  <button
                    onClick={() => {
                      const d = po.order_date || po.created_at;
                      setEditDate(d ? d.split('T')[0] : '');
                      setEditingDate(true);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                ) : null}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Estado</p>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(po.status)}`}
            >
              {STATUS_LABELS[po.status] ? s(STATUS_LABELS[po.status]) : s(po.status)}
            </span>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Total</p>
            <p className="text-lg font-bold text-gray-900">{formatPrice(po.total_amount)}</p>
          </div>
        </div>

        {po.description ? (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 uppercase">Descripcion</p>
            <p className="text-sm text-gray-700">{s(po.description)}</p>
          </div>
        ) : null}

        {po.notes ? (
          <div className="mt-2">
            <p className="text-xs text-gray-500 uppercase">Notas</p>
            <p className="text-sm text-gray-700">{s(po.notes)}</p>
          </div>
        ) : null}

        {po.stock_already_loaded ? (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <p className="text-xs font-semibold text-amber-800">
              Stock ingresado por remito (no sumó al inventario)
            </p>
            <p className="text-sm text-amber-700">
              {po.remito_number ? <>Remito <strong>{s(po.remito_number)}</strong></> : 'Sin N° de remito'}
              {po.remito_date ? <> · {formatDate(po.remito_date)}</> : null}
            </p>
          </div>
        ) : null}
      </div>

      {/* Items section */}
      {po.order_type === 'standard' && po.items && po.items.length > 0 ? (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900 uppercase">Items</h4>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button size="sm" variant="secondary" onClick={downloadDetail}>
                <ClipboardList className="h-4 w-4 mr-1" />
                Enviar al proveedor
              </Button>
              {canWrite && canReceive ? (
                <Button size="sm" variant="secondary" onClick={() => setEditModalOpen(true)}>
                  <Pencil className="h-4 w-4 mr-1" />
                  Editar
                </Button>
              ) : null}
              {canWrite && canReceive ? (
                <Button size="sm" onClick={() => setReceiveModalOpen(true)}>
                  <Package className="h-4 w-4 mr-1" />
                  Recibir Mercaderia
                </Button>
              ) : null}
            </div>
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pedido
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recibido
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pendiente
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Costo unit.
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subtotal
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(() => {
                  // Agrupar por costo unitario (asc) y, dentro de cada grupo, ordenar por nombre
                  const itemName = (it: typeof po.items[number]) =>
                    it.product_name ? s(it.product_name) : it.custom_name ? s(it.custom_name) : '-';
                  const sorted = [...po.items].sort((a, b) => {
                    const ca = n(a.unit_cost);
                    const cb = n(b.unit_cost);
                    if (ca !== cb) return ca - cb;
                    return itemName(a).localeCompare(itemName(b), 'es', { sensitivity: 'base' });
                  });

                  const rows: JSX.Element[] = [];
                  let lastCost: number | null = null;
                  for (const item of sorted) {
                    const ordered = n(item.quantity_ordered);
                    const received = n(item.quantity_received);
                    const remaining = ordered - received;
                    const cost = n(item.unit_cost);

                    // Encabezado de grupo cuando cambia el costo
                    if (lastCost === null || cost !== lastCost) {
                      rows.push(
                        <tr key={`grp-${cost}`} className="bg-gray-50">
                          <td colSpan={6} className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Costo unitario {formatPrice(cost)}
                          </td>
                        </tr>
                      );
                      lastCost = cost;
                    }

                    rows.push(
                      <tr key={item.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {itemName(item)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          {s(ordered)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-700">
                          {s(received)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          {remaining > 0 ? (
                            <span className="text-orange-600 font-medium">{s(remaining)}</span>
                          ) : (
                            <span className="text-green-600">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          {formatPrice(item.unit_cost)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium">
                          {formatPrice(item.line_total)}
                        </td>
                      </tr>
                    );
                  }
                  return rows;
                })()}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Receive modal */}
      <Modal
        isOpen={receiveModalOpen}
        onClose={() => setReceiveModalOpen(false)}
        title="Recibir Mercaderia"
        size="lg"
      >
        <ReceiveItemsForm
          po={po}
          onSuccess={() => setReceiveModalOpen(false)}
        />
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Editar Orden de Compra"
        size="xl"
      >
        <CreatePurchaseOrderForm editPO={po} onSuccess={() => setEditModalOpen(false)} />
      </Modal>

    </div>
  );
}

// ===========================================================================
// Receive Items Form
// ===========================================================================

function ReceiveItemsForm({
  po,
  onSuccess,
}: {
  po: PurchaseOrder;
  onSuccess: () => void;
}) {
  // Only items with pending quantities
  const pendingItems = (po.items ?? []).filter(
    (item) => n(item.quantity_ordered) - n(item.quantity_received) > 0
  );

  const [quantities, setQuantities] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {};
    pendingItems.forEach((item) => {
      const remaining = n(item.quantity_ordered) - n(item.quantity_received);
      initial[item.id] = String(remaining);
    });
    return initial;
  });
  const [receiveNotes, setReceiveNotes] = useState('');

  const receivePO = useReceivePurchaseOrder();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const receiveItems: ReceiveItemData[] = pendingItems
      .filter((item) => n(quantities[item.id]) > 0)
      .map((item) => ({
        item_id: item.id,
        quantity_received: n(quantities[item.id]),
      }));

    if (receiveItems.length === 0) return;

    try {
      await receivePO.mutateAsync({
        poId: po.id,
        data: {
          items: receiveItems,
          notes: receiveNotes || undefined,
        },
      });
      onSuccess();
    } catch (error) {
      console.error('Error receiving items:', error);
    }
  };

  if (pendingItems.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        Todos los items ya fueron recibidos.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-gray-500">
        Editá cuánto recibís de cada producto. Por defecto viene la cantidad pendiente.
      </p>
      <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
        {pendingItems.map((item) => {
          const ordered = n(item.quantity_ordered);
          const received = n(item.quantity_received);
          const remaining = ordered - received;
          const unit = (item.quantity_unit as Unit) || 'unit';
          const unitLabel = UNIT_LABEL_SHORT[unit];
          const itemName = item.product_name
            ? s(item.product_name)
            : item.custom_name
              ? s(item.custom_name)
              : '-';

          return (
            <div key={item.id} className="border border-gray-200 rounded-xl p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-gray-900 min-w-0 flex-1 truncate">{itemName}</p>
                <div className="flex items-center gap-1 shrink-0">
                  <input
                    type="number"
                    value={quantities[item.id] ?? ''}
                    onChange={(e) => setQuantities({ ...quantities, [item.id]: e.target.value })}
                    min="0"
                    max={remaining}
                    step={unit === 'unit' ? '1' : 'any'}
                    className="w-24 text-sm text-right px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                  <span className="text-xs text-gray-400 w-6">{unitLabel}</span>
                </div>
              </div>
              <div className="flex gap-4 mt-1.5 text-xs text-gray-500">
                <span>Pedido: <strong className="text-gray-700">{s(ordered)} {unitLabel}</strong></span>
                <span>Recibido: <strong className="text-green-700">{s(received)}</strong></span>
                <span>Pendiente: <strong className="text-orange-600">{s(remaining)} {unitLabel}</strong></span>
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
        <textarea
          value={receiveNotes}
          onChange={(e) => setReceiveNotes(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
          placeholder="Notas de recepcion..."
        />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="submit" loading={receivePO.isPending}>
          Confirmar Recepcion
        </Button>
      </div>
    </form>
  );
}

// ===========================================================================
// Supplier Payment Form (pays against the supplier account, not a single PO)
// ===========================================================================

function SupplierPaymentForm({
  supplierId,
  pendingAmount,
  onSuccess,
}: {
  supplierId: number;
  pendingAmount: number;
  onSuccess: () => void;
}) {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [amount, setAmount] = useState('');
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [deductFromRegister, setDeductFromRegister] = useState(false);
  const [reference, setReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  const registerPayment = useRegisterSupplierPayment();
  const deletePayment = useDeleteSupplierPayment();
  const updatePayment = useUpdateSupplierPayment();
  const { data: payments } = useSupplierPayments(supplierId);

  // Edición de un pago del historial
  const [editingPayment, setEditingPayment] = useState<SupplierPayment | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editMethod, setEditMethod] = useState('cash');
  const [editDate, setEditDate] = useState('');
  const [editRef, setEditRef] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const openEditPayment = (p: SupplierPayment) => {
    setEditingPayment(p);
    setEditAmount(String(Math.abs(n(p.amount))));
    setEditMethod(p.payment_method);
    setEditDate((p.payment_date || '').slice(0, 10));
    setEditRef(p.reference || '');
    setEditNotes(p.notes || '');
  };

  const saveEditPayment = async () => {
    if (!editingPayment) return;
    const isAjuste = editingPayment.payment_method === 'ajuste';
    await updatePayment.mutateAsync({
      paymentId: editingPayment.id,
      data: {
        amount: n(parseCurrencyToRaw(editAmount)),
        payment_method: isAjuste ? undefined : editMethod,
        payment_date: editDate || undefined,
        reference: isAjuste ? undefined : (editRef || null),
        notes: editNotes || null,
      },
    });
    setEditingPayment(null);
  };

  const paymentMethodOptions = [
    { value: 'cash', label: 'Efectivo' },
    { value: 'transfer', label: 'Transferencia' },
    { value: 'card', label: 'Tarjeta' },
    { value: 'check', label: 'Cheque' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsedAmount = n(amount);
    if (parsedAmount <= 0) return;
    if (pendingAmount > 0 && parsedAmount > pendingAmount) return;

    // First click: show confirmation
    if (!confirmingPayment) {
      setConfirmingPayment(true);
      return;
    }

    // Second click: actually submit
    const payload: SupplierPaymentCreate = {
      amount: parsedAmount,
      payment_method: paymentMethod,
      payment_date: paymentDate || undefined,
      reference: reference || undefined,
      notes: paymentNotes || undefined,
      deduct_from_register: deductFromRegister,
    };

    try {
      await registerPayment.mutateAsync({ supplierId, data: payload });
      onSuccess();
    } catch (error) {
      console.error('Error registering payment:', error);
    }
  };

  return (
    <div className="space-y-5">
      {/* Balance info */}
      <div className={`${pendingAmount > 0 ? 'bg-red-50' : 'bg-green-50'} p-4 rounded-lg text-center`}>
        <p className={`text-xs ${pendingAmount > 0 ? 'text-red-600' : 'text-green-600'} uppercase`}>
          {pendingAmount > 0 ? 'Saldo pendiente' : 'Cuenta saldada'}
        </p>
        <p className={`text-2xl font-bold ${pendingAmount > 0 ? 'text-red-700' : 'text-green-700'}`}>
          {formatPrice(pendingAmount)}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Monto a pagar *</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={formatCurrencyLive(amount)}
              onChange={(e) => {
                setConfirmingPayment(false);
                setAmount(parseCurrencyToRaw(e.target.value));
              }}
              placeholder="0,00"
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
            />
          </div>
        </div>

        <Select
          label="Metodo de pago"
          options={paymentMethodOptions}
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
        />

        <Input
          type="date"
          label="Fecha de pago"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
        />

        <Input
          label="Referencia"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Nro. de transferencia, cheque, etc."
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
          <textarea
            value={paymentNotes}
            onChange={(e) => setPaymentNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
            placeholder="Notas del pago..."
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={deductFromRegister}
            onChange={(e) => setDeductFromRegister(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Descontar de la caja del dia</span>
        </label>

        <div className="flex justify-end gap-2 pt-4 border-t">
          {confirmingPayment ? (
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-700">
                Registrar pago de <span className="font-bold text-green-700">{formatPrice(n(amount))}</span>?
              </p>
              <Button type="submit" loading={registerPayment.isPending}>
                Confirmar
              </Button>
              <button
                type="button"
                onClick={() => setConfirmingPayment(false)}
                className="text-sm text-gray-500 hover:text-gray-700 font-medium"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <>
              {pendingAmount > 0 && n(amount) > pendingAmount ? (
                <p className="text-sm text-red-600">El monto no puede superar la deuda pendiente</p>
              ) : null}
              <Button type="submit" disabled={n(amount) <= 0 || (pendingAmount > 0 && n(amount) > pendingAmount) || pendingAmount <= 0}>
                Registrar Pago
              </Button>
            </>
          )}
        </div>
      </form>

      {/* Payment history */}
      {payments && payments.length > 0 ? (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 uppercase mb-2">Historial de pagos</h4>
          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Metodo</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ref.</th>
                  {isAdmin && <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-10"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-3 py-2 text-sm text-gray-700">{formatDate(payment.payment_date)}</td>
                    <td className={`px-3 py-2 text-sm text-right font-medium ${payment.payment_method === 'ajuste' ? 'text-amber-700' : 'text-green-700'}`}>{formatPrice(payment.amount)}</td>
                    <td className="px-3 py-2 text-sm text-gray-700">
                      {payment.payment_method === 'ajuste' ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">Ajuste</span>
                      ) : (
                        PAYMENT_METHOD_LABELS[payment.payment_method] ? s(PAYMENT_METHOD_LABELS[payment.payment_method]) : s(payment.payment_method)
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600">
                      {payment.payment_method === 'ajuste' && payment.notes ? s(payment.notes) : (payment.reference ? s(payment.reference) : '-')}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-center">
                        {deletingPaymentId === payment.id ? (
                          <div className="flex items-center gap-1 justify-center">
                            <button
                              onClick={async () => {
                                await deletePayment.mutateAsync(payment.id);
                                setDeletingPaymentId(null);
                              }}
                              className="text-xs text-red-600 font-medium hover:text-red-800"
                            >
                              Si
                            </button>
                            <span className="text-gray-400">|</span>
                            <button
                              onClick={() => setDeletingPaymentId(null)}
                              className="text-xs text-gray-500 font-medium hover:text-gray-700"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 justify-center">
                            <button
                              onClick={() => openEditPayment(payment)}
                              className="text-gray-400 hover:text-primary-600"
                              title="Editar pago"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDeletingPaymentId(payment.id)}
                              className="text-red-400 hover:text-red-600"
                              title="Eliminar pago"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Modal editar pago del historial */}
      <Modal
        isOpen={editingPayment !== null}
        onClose={() => setEditingPayment(null)}
        title={editingPayment?.payment_method === 'ajuste' ? 'Editar ajuste' : 'Editar pago'}
        size="sm"
      >
        {editingPayment ? (
          <div className="space-y-4">
            {editingPayment.payment_method === 'ajuste' && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                Es un ajuste de deuda. Se conserva el signo (a favor / en contra); solo cambia el monto, la fecha y el motivo.
              </p>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monto</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatCurrencyLive(editAmount)}
                  onChange={(e) => setEditAmount(parseCurrencyToRaw(e.target.value))}
                  className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400"
                />
              </div>
            </div>

            {editingPayment.payment_method !== 'ajuste' && (
              <Select
                label="Método de pago"
                options={paymentMethodOptions}
                value={editMethod}
                onChange={(e) => setEditMethod(e.target.value)}
              />
            )}

            <Input
              type="date"
              label="Fecha"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
            />

            {editingPayment.payment_method !== 'ajuste' && (
              <Input
                label="Referencia"
                value={editRef}
                onChange={(e) => setEditRef(e.target.value)}
                placeholder="Nro. de transferencia, cheque, etc."
              />
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {editingPayment.payment_method === 'ajuste' ? 'Motivo / notas' : 'Notas'}
              </label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400"
              />
            </div>

            {editingPayment.cash_withdrawal_id ? (
              <p className="text-xs text-gray-500">Este pago descontó de caja: al cambiar el monto se ajusta también la salida de caja.</p>
            ) : null}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="secondary" onClick={() => setEditingPayment(null)}>Cancelar</Button>
              <Button
                onClick={saveEditPayment}
                loading={updatePayment.isPending}
                disabled={n(parseCurrencyToRaw(editAmount)) <= 0}
              >
                Guardar cambios
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

interface ProductSearchSelectProps {
  value: string;
  searchValue?: string;
  isOpen: boolean;
  results: Array<{ id: number; name: string }>;
  isSearching: boolean;
  onFocus: () => void;
  onSearch: (val: string) => void;
  onSelect: (product: { id: number; name: string }) => void;
}

function ProductSearchSelect({ value, searchValue, isOpen, results, isSearching, onFocus, onSearch, onSelect }: ProductSearchSelectProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 350),
        zIndex: 9999,
      });
    }
  }, [isOpen]);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="text"
        value={isOpen ? (searchValue ?? '') : value}
        onChange={(e) => onSearch(e.target.value)}
        onFocus={onFocus}
        placeholder="Buscar producto..."
        className="w-full text-sm px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 outline-none"
      />
      {isOpen && (
        <div
          style={dropdownStyle}
          className="bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-y-auto"
        >
          {results
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((p) => (
              <button
                key={p.id}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 hover:text-primary-700"
                onClick={() => onSelect(p)}
              >{p.name}</button>
            ))}
          {results.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-400">
              {isSearching ? 'Sin resultados' : 'Cargando...'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
